import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { RotateCcw, ShieldCheck } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { SummaryRow } from '@/components/ui/detail';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { LoadingState } from '@/components/ui/states';
import {
  FLEET_PERMISSIONS,
  FLEET_ROUTES,
  type PrintJobDetail,
} from '../fleetApi';
import { AttemptOutcomeChip, JobStatusChip, PrinterStatusChip } from '../fleetVisuals';
import { apiMessage, formatDateTime } from '../fleetHelpers';

/**
 * One job, with every delivery attempt against it.
 *
 * This is the answer to "which printer failed and what did it say", and the
 * attempt log is why it can be answered at all: without `PrintJobAttempt` the
 * only record of a failure lives in application logs, which is exactly the
 * thing this epic exists to stop operators needing.
 *
 * The rendered document is **not** shown, and its absence is deliberate rather
 * than an omission. A print job can carry a guest's name, their table and what
 * they ordered; a diagnostics panel is read over shoulders, and a terminal
 * job's payload is nulled by the retention sweep after seven days anyway. What
 * is shown is status, attempts and errors — the record, not the guest.
 */
export default function PrintJobDetailDialog({
  jobId,
  onClose,
  onChanged,
}: {
  jobId: string | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const [busy, setBusy] = useState(false);

  const query = useApi<PrintJobDetail>(jobId ? FLEET_ROUTES.job(jobId) : null);
  const job = query.data;

  const canRetry = can(FLEET_PERMISSIONS.jobsRetry);
  const retryable = job ? job.status === 'FAILED' || job.status === 'DEAD' : false;

  const retry = async () => {
    if (!job) return;
    setBusy(true);
    try {
      await api.post(FLEET_ROUTES.jobRetry(job.id));
      toast.success(t('fleet.spool.retried'));
      query.reload();
      onChanged();
    } catch (error) {
      toast.error(apiMessage(error, t('fleet.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={jobId !== null}
      onClose={onClose}
      title={t('fleet.jobDetail.title')}
      description={t('fleet.jobDetail.subtitle')}
      className="max-w-3xl"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          {canRetry && retryable && (
            <Button variant="outline" disabled={busy} onClick={() => void retry()}>
              <Icon as={RotateCcw} />
              {t('fleet.spool.retry')}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      }
    >
      {query.loading ? (
        <LoadingState />
      ) : query.error ? (
        /* Not the empty state. A job that could not be read and a job with no
           attempts must not look alike — see `PanelResult`. */
        <div
          role="alert"
          className="rounded-lg border border-red-300 bg-red-50/70 p-4 text-sm dark:border-red-900 dark:bg-red-950/30"
        >
          <p className="font-medium">{t('fleet.states.loadFailed', { subject: t('fleet.jobDetail.subject') })}</p>
          <p className="mt-1 text-ink-muted">{query.error}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={query.reload}>
            {t('common.tryAgain')}
          </Button>
        </div>
      ) : job ? (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <SummaryRow label={t('fleet.common.status')}>
                <JobStatusChip status={job.status} />
              </SummaryRow>
              <SummaryRow label={t('fleet.spool.document')}>
                {t(`fleet.documentType.${job.documentType}`)}
              </SummaryRow>
              <SummaryRow label={t('fleet.common.destination')}>
                {job.destination?.name ?? t('fleet.common.unknownDestination')}
              </SummaryRow>
              <SummaryRow label={t('fleet.common.printer')}>
                {job.printer?.name ?? t('fleet.spool.printerAtClaim')}
              </SummaryRow>
              <SummaryRow label={t('fleet.common.bridge')}>
                {job.bridge?.name ?? t('fleet.common.dash')}
              </SummaryRow>
            </div>
            <div className="space-y-1.5">
              <SummaryRow label={t('fleet.spool.attempts')}>
                {t('fleet.spool.attemptsOf', { attempts: job.attempts, max: job.maxAttempts })}
              </SummaryRow>
              <SummaryRow label={t('fleet.spool.queuedAt')}>
                {formatDateTime(job.queuedAt, i18n.language)}
              </SummaryRow>
              <SummaryRow label={t('fleet.jobDetail.printedAt')}>
                {job.printedAt ? formatDateTime(job.printedAt, i18n.language) : t('fleet.common.dash')}
              </SummaryRow>
              <SummaryRow label={t('fleet.jobDetail.failedAt')}>
                {job.failedAt ? formatDateTime(job.failedAt, i18n.language) : t('fleet.common.dash')}
              </SummaryRow>
              <SummaryRow label={t('fleet.jobDetail.nextAttemptAt')}>
                {/* The backoff gate. A failed job is invisible to a bridge until
                    its slot elapses, so "nothing is happening" is often "not
                    yet" rather than "stuck". */}
                {job.status === 'FAILED'
                  ? formatDateTime(job.availableAt, i18n.language)
                  : t('fleet.common.dash')}
              </SummaryRow>
            </div>
          </div>

          {job.lastError && (
            <div className="rounded-lg border border-red-300 bg-red-50/70 p-3 text-sm dark:border-red-900 dark:bg-red-950/30">
              <p className="font-medium">{t('fleet.spool.whatWentWrong')}</p>
              <p className="mt-1 break-words text-ink-muted">{job.lastError}</p>
            </div>
          )}

          {/* The retention sweep nulls a terminal job's document after seven
              days. The row survives — an operator reading last week's failures
              needs the record, not the guest's name — but a reprint of it is
              refused rather than printing a blank ticket, so say so here instead
              of letting them find out from a button. */}
          {job.payloadScrubbedAt && (
            <p className="rounded-lg border border-dashed p-3 text-sm text-ink-muted">
              {t('fleet.jobDetail.scrubbed', {
                time: formatDateTime(job.payloadScrubbedAt, i18n.language),
              })}
            </p>
          )}

          {job.isReprint && (
            <p className="text-sm text-ink-muted">{t('fleet.jobDetail.isReprintOf')}</p>
          )}
          {job.reprints.length > 0 && (
            <p className="text-sm text-ink-muted">
              {t('fleet.jobDetail.hasReprints', { count: job.reprints.length })}
            </p>
          )}

          <div className="space-y-2">
            <h4 className="text-sm font-semibold">{t('fleet.jobDetail.attemptLog')}</h4>
            {job.attemptLog.length === 0 ? (
              <p className="rounded-lg border border-dashed p-4 text-sm text-ink-muted">
                {t('fleet.jobDetail.noAttempts')}
              </p>
            ) : (
              <TableWrap>
                <Table>
                  <THead>
                    <TR>
                      <TH>{t('fleet.jobDetail.attemptNumber')}</TH>
                      <TH>{t('fleet.jobDetail.outcome')}</TH>
                      <TH>{t('fleet.jobDetail.printerSaid')}</TH>
                      <TH>{t('fleet.jobDetail.detail')}</TH>
                      <TH>{t('fleet.jobDetail.at')}</TH>
                    </TR>
                  </THead>
                  <tbody>
                    {job.attemptLog.map((attempt, index) => (
                      <TR key={`${attempt.attempt}-${index}`}>
                        <TD className="tabular-nums">{attempt.attempt}</TD>
                        <TD>
                          <AttemptOutcomeChip outcome={attempt.outcome} />
                          {attempt.outcome === 'LATE' && (
                            /* The only evidence an operator has that a second
                               copy may exist. Said plainly rather than left as a
                               five-letter enum nobody has met before. */
                            <p className="mt-1 max-w-[16rem] text-xs text-ink-muted">
                              {t('fleet.jobDetail.lateExplainer')}
                            </p>
                          )}
                        </TD>
                        <TD>
                          {attempt.printerStatus ? (
                            <PrinterStatusChip status={attempt.printerStatus} />
                          ) : (
                            <span className="text-ink-muted">{t('fleet.common.dash')}</span>
                          )}
                        </TD>
                        <TD className="max-w-[18rem] break-words text-ink-muted">
                          {attempt.detail ?? t('fleet.common.dash')}
                        </TD>
                        <TD className="text-ink-muted">
                          {formatDateTime(attempt.createdAt, i18n.language)}
                        </TD>
                      </TR>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </div>

          <p className="flex items-start gap-2 text-xs text-ink-muted">
            <Icon as={ShieldCheck} className="mt-0.5 shrink-0" />
            {t('fleet.jobDetail.noPayloadNote')}
          </p>
        </div>
      ) : null}
    </Dialog>
  );
}
